/**
 * Utilidades compartidas para parseo e importación de CSV.
 * Usadas por ImportCSVMapperModal y BankStatementUploadModal.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SystemField {
  key: string
  label: string
  required?: boolean
  aliases?: string[]
}

// ─── Separator detection ─────────────────────────────────────────────────────

export function detectSeparator(text: string): string {
  const firstLine = text.split(/\r?\n/)[0] ?? ''
  const commas = (firstLine.match(/,/g) ?? []).length
  const semicolons = (firstLine.match(/;/g) ?? []).length
  return semicolons > commas ? ';' : ','
}

// ─── CSV Parser (handles quoted fields, no external deps) ────────────────────

export function parseCSVLine(line: string, separator = ','): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === separator) {
        fields.push(current)
        current = ''
      } else {
        current += ch
      }
    }
  }
  fields.push(current)
  return fields
}

/**
 * Detecta heurísticamente la fila de headers saltándose filas de preámbulo
 * (título del reporte, metadata del banco, líneas vacías con separadores).
 *
 * Estrategia: analiza las primeras 10 filas no vacías, cuenta celdas con
 * contenido en cada una, toma como headers la PRIMERA fila con ≥ 80% del
 * ancho máximo observado. Esto maneja exports bancarios que meten 1-3 filas
 * de metadata antes de los headers reales.
 */
function detectHeaderRow(parsedLines: string[][]): number {
  const candidates = parsedLines.slice(0, 10)
  if (candidates.length === 0) return 0
  const maxWidth = Math.max(...candidates.map(r => r.filter(c => c.trim() !== '').length))
  if (maxWidth <= 1) return 0
  const threshold = Math.max(2, Math.floor(maxWidth * 0.8))
  for (let i = 0; i < candidates.length; i++) {
    const nonEmpty = candidates[i].filter(c => c.trim() !== '').length
    if (nonEmpty >= threshold) return i
  }
  return 0
}

/**
 * Hace únicas las cabeceras: reemplaza vacías por `_col_N` y añade sufijo
 * numérico a duplicados. Mantiene la mayoría intactas; solo se dispara cuando
 * el CSV viene con headers rotos.
 */
function dedupeHeaders(raw: string[]): string[] {
  const seen = new Map<string, number>()
  return raw.map((h, idx) => {
    const base = h.trim() || `_col_${idx + 1}`
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    return count === 0 ? base : `${base}_${count + 1}`
  })
}

export function parseCSVPreview(
  text: string,
  separator?: string,
): {
  headers: string[]
  rows: string[][]
  detectedSeparator: string
  skipRows: number
  /** Filas de datos del archivo completo (sin encabezado). Se usa para avisar el
   *  tamaño ANTES de importar: una importación de cientos de filas tarda minutos. */
  totalRows: number
} {
  const clean = text.replace(/^\uFEFF/, '')
  const sep = separator ?? detectSeparator(clean)
  const lines = clean.split(/\r?\n/).filter(l => l.trim() !== '')
  if (lines.length === 0) return { headers: [], rows: [], detectedSeparator: sep, skipRows: 0, totalRows: 0 }

  const parsed = lines.map(l => parseCSVLine(l, sep).map(v => v.trim()))
  const headerIdx = detectHeaderRow(parsed)
  const rawHeaders = parsed[headerIdx] ?? []
  const headers = dedupeHeaders(rawHeaders)
  const rows = parsed.slice(headerIdx + 1, headerIdx + 4)
  return { headers, rows, detectedSeparator: sep, skipRows: headerIdx,
           totalRows: Math.max(parsed.length - headerIdx - 1, 0) }
}

// ─── Auto-match logic ────────────────────────────────────────────────────────

export function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/[\s_-]+/g, '_')
}

// ─── Date format detection ───────────────────────────────────────────────────

export interface DateFormatOption {
  pattern: string
  label: string
  example: string
}

export const DATE_FORMATS: DateFormatOption[] = [
  { pattern: 'DD/MM/YYYY', label: 'DD/MM/AAAA', example: '31/12/2026' },
  { pattern: 'YYYY-MM-DD', label: 'AAAA-MM-DD (ISO)', example: '2026-12-31' },
  { pattern: 'MM/DD/YYYY', label: 'MM/DD/AAAA', example: '12/31/2026' },
  { pattern: 'DD-MM-YYYY', label: 'DD-MM-AAAA', example: '31-12-2026' },
]

export function detectDateFormat(sampleValues: string[]): string {
  for (const val of sampleValues) {
    const v = val.trim()
    // YYYY-MM-DD or YYYY/MM/DD
    if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(v)) return 'YYYY-MM-DD'
    // DD/MM/YYYY — check if day > 12 to disambiguate from MM/DD/YYYY
    const m = v.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/)
    if (m) {
      const first = parseInt(m[1], 10)
      if (first > 12) return 'DD/MM/YYYY'
    }
  }
  // If all ambiguous (day <= 12), default to DD/MM/YYYY (LATAM default)
  const hasSlashOrDash = sampleValues.some(v => /^\d{2}[/-]\d{2}[/-]\d{4}$/.test(v.trim()))
  if (hasSlashOrDash) return 'DD/MM/YYYY'
  return 'YYYY-MM-DD'
}

// ─── Auto-match logic ────────────────────────────────────────────────────────

export function autoMatch(
  csvHeaders: string[],
  systemFields: SystemField[],
): Record<string, string> {
  const mapping: Record<string, string> = {}
  const usedFields = new Set<string>()

  for (const header of csvHeaders) {
    const norm = normalize(header)
    for (const sf of systemFields) {
      if (usedFields.has(sf.key)) continue
      const allAliases = [sf.key, normalize(sf.label), ...(sf.aliases || []).map(normalize)]
      if (allAliases.includes(norm)) {
        mapping[header] = sf.key
        usedFields.add(sf.key)
        break
      }
    }
  }
  return mapping
}
